/**
 * Internal contracts for Epic #84 P7 — batch allocator + capacity sync +
 * threshold alert + gateway selector capacity filter.
 *
 * Three logical pipelines, four endpoints:
 *
 *   1. **Batch allocator** (every 5 min)
 *      - control-loops → platform `/internal/ops/batch-allocate`
 *      - platform → gateway `/internal/usage/pending` (read)
 *      - platform → gateway `/internal/usage/mark-allocated` (write)
 *
 *   2. **Capacity depletion sync** (every 5 min, after allocator)
 *      - control-loops → platform `/internal/ops/capacity-sync`
 *      - platform → gateway `/internal/upstreams/capacity-status` (write)
 *
 *   3. **Batch threshold alert** (every 5 min, after allocator)
 *      - control-loops → platform `/internal/ops/batch-threshold-check`
 *
 * All endpoints are shared-secret bearer only; no tenant surface. See
 * ADR-004 for internal-plane conventions.
 */

import type { BrandId } from './brands.ts';
import type { CreditThresholdAlert, H1AcctReadFallbackNotice, PostpaidReadErrors } from './internal-upstream-credit-terms.ts';
import type { LedgerWriteFreezeSummary } from './internal-balance-sources.ts';

// ─── Gateway `/internal/usage/pending` ─────────────────────────────────

/**
 * One unallocated usage_logs row surfaced to the allocator. The gateway
 * returns its own `usage_logs.id` (platform uses it for the mark-allocated
 * callback) + the `request_id` that lets platform cross-ref
 * `balance_transactions.external_request_id` → `tx_id` (the platform-side
 * transaction id that `revenue_ledger.tx_id` points at).
 *
 * `cost_usd_micros` is the gateway's authoritative tenant charge amount.
 * `cost_usd_cents` is retained as the compatibility projection. Platform
 * re-reads `revenue_ledger.usd_charged_micros` (placeholder row written at
 * charge time) for revenue, while the cost breakdown and token/model fields
 * let the allocator compute real upstream cost independently from tenant
 * price.
 *
 * The token/model fields are optional for rolling deploy compatibility:
 * during a gateway/platform version skew, platform falls back to the legacy
 * "charged cents × batch FX" allocation for rows that do not carry them.
 */
export interface PendingUsageRow {
  readonly id: string;
  readonly request_id: string;
  readonly upstream_id: string;
  readonly cost_usd_cents: number;
  readonly cost_usd_micros?: number;
  readonly created_at: string;
  readonly api_key_id?: string | null;
  readonly vendor?: string | null;
  readonly model?: string | null;
  readonly sku_id?: string | null;
  readonly sku_name?: string | null;
  readonly input_tokens?: number;
  readonly output_tokens?: number;
  readonly cache_read_tokens?: number;
  readonly cache_write_tokens?: number;
  readonly usage_details_json?: string | null;
  readonly tenant_cost_breakdown_json?: string | null;
  readonly upstream_cost_breakdown_json?: string | null;
  readonly upstream_account_id?: string | null;
  readonly upstream_key_bucket_id?: string | null;
  readonly upstream_channel_id?: string | null;
  readonly upstream_channel_slug?: string | null;
  /** Exact new-write procurement snapshot keyed by upstream + upstream channel + SKU. */
  readonly upstream_sku_cost_multiplier?: number | null;
  /** Historical pre-SKU snapshot; read only when the new field is absent. */
  readonly upstream_channel_cost_multiplier?: number | null;
  readonly upstream_account_rate_multiplier?: number | null;
  readonly cost_snapshot_source?: string | null;
  /**
   * Peak/off-peak overlay that produced `upstream_sku_cost_multiplier`.
   * OPTIONAL: historical rows and gateways that predate the column send
   * nothing, which the allocator reads as "no window was in force".
   */
  readonly upstream_cost_window_id?: string | null;
  readonly ingress_brand?: BrandId | null;
  /**
   * `usage_logs.billing_outcome` (#1410, task D18). OPTIONAL because the
   * gateway is the producer and may predate this field; `undefined` therefore
   * means "not pending", i.e. today's behaviour, so a rolling deploy never
   * turns into a stall.
   *
   * The one value the allocator must treat specially is `'rating_pending'`: it
   * is a THIRD meaning for a missing revenue_ledger placeholder, alongside the
   * two the placeholder-less branch already handles (genuine zero-cost usage,
   * and a tiny commit race). Unlike those two, it must NOT be terminal —
   * marking such a row allocated loses its procurement attribution
   * permanently, which is the #1501 defect class.
   */
  readonly billing_outcome?: string;
}

export interface PendingUsageResponse {
  readonly rows: readonly PendingUsageRow[];
  /**
   * Echo of the `shard` / `shards` query (P1a): present exactly when the
   * request carried them and the page was claimed from that shard only. A
   * gateway that predates P1a ignores the query and omits this, which the
   * platform reads as "shard not honored" and refuses to commit the page.
   */
  readonly shard?: BatchAllocateShard;
}

// ─── Gateway `/internal/usage/mark-allocated` ──────────────────────────

export interface MarkAllocatedRequest {
  readonly ids: readonly string[];
}

export interface MarkAllocatedResponse {
  /**
   * Rows that genuinely left the gateway's pending queue on this call.
   * Since #2524 step 4 the gateway records "allocated" by dequeuing only;
   * `usage_logs.allocated_at` is no longer written.
   */
  readonly marked: number;
}

// ─── Gateway `/internal/upstreams/capacity-status` ─────────────────────

/**
 * One capacity-status update. `upstream_id` is the gateway-db
 * `upstream_configs.id`; `capacity_status` is the target state the
 * platform capacity-sync computed from batch exhaustion.
 *
 * The gateway applies these with a `CASE` guard that preserves
 * `capacity_override_until` — when an ops override is active, the write
 * is a no-op even if we claim `depleted`. This keeps the ops
 * "emergency continue" lever decoupled from the cron's automatic path.
 */
export interface CapacityStatusUpdate {
  readonly upstream_id: string;
  /**
   * When present, the update applies to `upstream_accounts`; when absent, the
   * legacy upstream-level capacity column is updated for rolling compatibility.
   */
  readonly account_id?: string | null;
  readonly capacity_status: 'healthy' | 'depleted';
}

export interface CapacityStatusRequest {
  readonly updates: readonly CapacityStatusUpdate[];
}

export interface CapacityStatusResponse {
  readonly applied: number;
}

/**
 * One row from gateway `GET /internal/upstreams/capacity-states`. Used by
 * platform-api's capacity-sync handler to compute `from → to` transitions
 * so the ops_audit row is precise (no "flipped" claim without a reference
 * pre-state). Limited to the two capacity fields + id — the larger
 * `/internal/upstreams` list returns the full UpstreamConfig contract
 * which does not carry these columns.
 */
export interface CapacityStateRow {
  readonly upstream_id: string;
  readonly account_id?: string | null;
  readonly capacity_status: 'healthy' | 'depleted';
  readonly capacity_override_until: string | null;
}

export interface CapacityStatesResponse {
  readonly states: readonly CapacityStateRow[];
}

// ─── Platform `/internal/ops/batch-allocate` ───────────────────────────

/**
 * Largest page the allocation pipeline will move in one request.
 *
 * This bound is enforced independently at three points — the gateway's
 * `/internal/usage/pending` reader, platform's allocator, and the
 * control-loops caller that sizes its drain pages — and they MUST agree.
 * If a caller asks for more than a server-side cap, the request is
 * silently clamped and every page comes back "short", which is exactly
 * the signal the drain loop reads as "the queue is empty". A backlog
 * would then stop draining while reporting success. One shared constant
 * removes the possibility of those three numbers drifting apart.
 */
export const BATCH_ALLOCATE_MAX_LIMIT = 500;

/**
 * The allocator route of a platform that re-carves every commit under the
 * batch locks (#2688) and bounds every page (batch 2 review A1). Capability
 * negotiation for the rollout (A10): control-loops calls this path first, and
 * a platform that does not have it answers 404 — which control-loops reads as
 * "old platform" and falls back to the legacy path, legacy request body and
 * the legacy 5-minute cadence. Its 200 bodies carry `lockedRecarve: true`; a
 * 200 without it is treated like a 404. Same handler as the legacy path on a
 * platform that has both.
 */
export const BATCH_ALLOCATE_LOCKED_RECARVE_PATH = '/internal/ops/batch-allocate/v2';

// ─── Allocator shards (P1a, router#2539, billing-chain-150k §4) ─────────

/**
 * One allocator shard: the usage rows whose request id hashes to `index`
 * modulo `count` (gateway claim:
 * `((hashtextextended(request_id, 0) % count) + count) % count = index`).
 * Hashing the REQUEST id, not the usage-log id, keeps every usage row of one
 * request in one shard, where the platform's same-tx dedupe already sees
 * them together. Disjointness is an efficiency property only: the platform's
 * anchor locks, gated writes and locked re-carve keep the books right when
 * two callers do hold the same row (a K change mid-tick, a manual trigger).
 */
export interface BatchAllocateShard {
  readonly index: number;
  readonly count: number;
}

/**
 * Largest shard count the WIRE accepts (gateway `/pending`, platform
 * `/batch-allocate`). Above the control plane's ceiling on purpose, so a later
 * driver with a higher one (the billing loop, design §4.2: 8) needs no gateway
 * or platform release. Anything outside [1, max] is a 400, never ignored: an
 * ignored shard hands out the whole page.
 */
export const BATCH_ALLOCATE_MAX_SHARDS = 16;

/** The shard `index` / `count` name when the wire accepts it, else `null`. */
export function toBatchAllocateShard(index: unknown, count: unknown): BatchAllocateShard | null {
  if (typeof index !== 'number' || typeof count !== 'number') return null;
  if (!Number.isSafeInteger(index) || !Number.isSafeInteger(count)) return null;
  if (count < 1 || count > BATCH_ALLOCATE_MAX_SHARDS || index < 0 || index >= count) return null;
  return { index, count };
}

/**
 * Hard ceiling on `BATCH_ALLOCATOR_CONSUMERS` (P1a, billing-chain-150k §4.2):
 * default 1 (today's single drain, byte for byte), at most 4; anything above
 * or not an integer falls back to 1 with a `configWarning`. Who drives the
 * drain then caps it (the R1 rule of `RATING_DRAIN_CONSUMERS`): the Cloudflare
 * tick and its Node fallback ticker at {@link BATCH_ALLOCATOR_CONSUMERS_TICK_CAP}
 * (4), the billing loop at {@link BATCH_ALLOCATOR_CONSUMERS_LOOP_CAP} (2); a
 * value above the driver's cap runs at the cap with a `configWarning`. K >= 2
 * runs K shard loops concurrently inside one `runBatchAllocator` call, each
 * POSTing `{ limit, commitChunkSize, shard: { index, count: K } }`.
 *
 * The process page slots of the platform (`BATCH_ALLOCATE_SHARDED_MAX_
 * CONCURRENT_PAGES`, platform-api) are a separate constant with their own pool
 * reasoning; raising this ceiling does not raise them.
 */
export const BATCH_ALLOCATOR_CONSUMERS_CEILING = 4;

/**
 * K's cap under the Cloudflare tick and the Node fallback ticker. The minimum
 * of:
 *
 *   a. Cloudflare: at most 6 connections per invocation waiting for response
 *      headers (the bound RATING_DRAIN_CONSUMERS_TICK_CAP derives, a). The
 *      tick runs its serial handlers one at a time, so the tick's rating lanes
 *      never run beside the shards; the one thing that does is the credit-draw
 *      sweep lane (#2854, `concurrent`, 1 connection for its whole share):
 *      K + 1 <= 6, and 4 keeps one spare for a response body still streaming.
 *   b. Subrequests: K x ROUNDS_CEILING (300) = 1 200 at K = 4 of 10 000 per
 *      invocation; with the rating lanes' 1 600 the tick stays far below.
 *   c. Platform pool (`PLATFORM_PG_POOL_MAX`, 20 in production): an allocator
 *      page holds at most one pooled connection at a time (A1). Worst case
 *      every shard lands on one host beside the backstop sweep (1 + 4), an
 *      abandoned credit-draw catch-up round (4), the credit-draw sweep lane (2)
 *      and >= 2 for tenant / ops traffic: 4 + 5 + 4 + 2 + 2 = 17 <= 20. Rating
 *      lanes driven by the same tick do not overlap (serial dispatch). Rating
 *      lanes driven by the billing loop WOULD (2 K_r more): that combination
 *      (rating on the loop, allocator on the tick) needs K x 1 + 2 K_r + 13 <=
 *      the pool — at 20, K_r = 2 leaves K <= 3. Not reachable today (the loop
 *      does not drive rating); docs/runbooks/rating-rollout.md, "Allocator
 *      shards", has the table.
 *   d. Gateway pool (`GATEWAY_POSTGRES_POOL_MAX`, default 10, ceiling 32): a
 *      shard's `/pending` holds one connection (the repair / sweep transaction,
 *      then the claim), its `/mark-allocated` one; K <= 4 on one host next to
 *      tenant traffic.
 *   e. The hot funding batch: every shard's commit locks the batches of the
 *      page's upstreams, so the effective parallelism is about
 *      page time / batch-lock time; shards beyond it only add lock wait.
 *      Measured locally: lock wait about 1/5 of commit time at K = 4.
 *   f. The 55 s handler bound holds per shard: each checks DRAIN_BUDGET_MS
 *      before a round and a round is bounded by ROUND_TIMEOUT_MS, so the
 *      Promise.all of K shards ends by 55 s.
 */
export const BATCH_ALLOCATOR_CONSUMERS_TICK_CAP = 4;

/**
 * K's cap under the billing loop (#2539 L2, `BILLING_LOOPS` naming
 * `allocator`). The loop reads the SAME `BATCH_ALLOCATOR_CONSUMERS` from
 * `platform.env` and sends every shard to its own host's platform — every
 * shard on one host is the normal case, not the worst — and it runs beside the
 * Cloudflare tick's rating lanes, continuously. Pool (`PLATFORM_PG_POOL_MAX`
 * 20, the tenant platform process; there is no separate billing worker yet):
 *
 *   2 K_r rating lanes (2 x 2 in production) + K allocator pages + backstop
 *   sweep 5 + abandoned catch-up 4 + credit-draw sweep lane 2 + tenant 2
 *   = 4 + K + 13 <= 20  =>  K <= 3; 2 keeps one spare.
 *
 * At K_r = 3 the sum is 21 with K = 2: raise the pool to 22 first (runbook).
 * Raising this cap needs a separate pool for the loop's work (design §3.4,
 * `platform-billing`), not just a bigger number here.
 */
export const BATCH_ALLOCATOR_CONSUMERS_LOOP_CAP = 2;

/**
 * The allocator is stateless — control-loops POSTs an empty body; platform
 * decides internally which usage rows to process. An explicit `limit` is
 * reserved here so a future test or backfill script can batch-tune page
 * size without a contract change.
 */
export interface BatchAllocateRequest {
  readonly limit?: number;
  /** Plans per commit transaction; control-loops sends the page size (P0). */
  readonly commitChunkSize?: number;
  /**
   * P1a: claim from this shard only (`BATCH_ALLOCATOR_CONSUMERS` >= 2). Absent
   * — every K = 1 request — the page is today's, byte for byte.
   */
  readonly shard?: BatchAllocateShard;
}

/**
 * Allocator outcome. The handler is crash-safe end-to-end — even when the
 * gateway read or callback fails mid-flight, the response shape is stable.
 *
 *   - `usageRowsProcessed`: how many gateway rows we touched (including
 *     skipped-idempotent rows where revenue_ledger was already filled).
 *   - `revenueLedgerRowsWritten`: total rows inserted into revenue_ledger
 *     this tick, summed across all usage rows (spillover means N rows per
 *     usage row).
 *   - `batchesDepleted`: count of upstream_funding_batch rows whose
 *     is_depleted flipped 0→1 this tick.
 *   - `overflowCount`: usage rows that could not be allocated because all
 *     batches for the upstream were exhausted. Caller logs this; the
 *     capacity-sync cron flips the upstream to 'depleted' next tick.
 *   - `markedAllocated`: how many rows the gateway callback reported it
 *     dequeued from `usage_allocation_pending` (#2524 step 4: no stamp).
 *     < usageRowsProcessed means some ids had already left the queue
 *     (a concurrent allocator, a retry) — benign under the idempotent
 *     model.
 */
export interface BatchAllocateResponse {
  readonly ok: true;
  readonly usageRowsProcessed: number;
  readonly revenueLedgerRowsWritten: number;
  readonly batchesDepleted: number;
  readonly overflowCount: number;
  /** Current SKU rows held without funding consumption because procurement evidence is missing. */
  readonly quarantinedCount: number;
  readonly markedAllocated: number;
  /**
   * Rows withheld this tick because their sale has not been rated yet
   * (#1410, task D18). Optional for rolling-deploy compatibility. Non-zero is
   * NORMAL while rating is a few seconds behind; a value that stays high
   * across ticks means rating is stuck and procurement attribution is
   * accumulating unallocated — visible here rather than as a silent skip.
   */
  readonly ratingPendingSkipped?: number;
  /**
   * The ANOMALOUS subset of `ratingPendingSkipped` (#1867) that a backfill can
   * clear: rows rated `insufficient_balance` more than
   * `STALLED_NO_PLACEHOLDER_MIN_AGE_MS` ago that still have no `revenue_ledger`
   * placeholder. Optional for rolling-deploy compatibility.
   *
   * Withholding a row whose sale has not been rated yet is normal and
   * self-clearing; withholding one that HAS been rated is not, because nothing
   * further will ever create its placeholder. Those rows sit at the head of the
   * FIFO pending page and, at 521 of them against a 500-row page, stalled
   * procurement attribution outright for 47 hours while the cron kept reporting
   * `ok: true`.
   *
   * They are deliberately NOT auto-marked allocated: that would discard the
   * procurement attribution permanently, which is the #1501 defect class. This
   * counter only makes them visible; `POST
   * /internal/ops/unpaid-placeholder-backfill` is what clears them.
   *
   * A GAUGE, not a counter — see the note below.
   */
  readonly stalledNoPlaceholder?: number;
  /**
   * The other anomalous subset (#1867): rows rated more than
   * `STALLED_NO_PLACEHOLDER_MIN_AGE_MS` ago with no placeholder and a rating
   * outcome the `limit` scan does NOT write — `zero_cost`, `quarantined`,
   * `unpriced`, `charged`. Disjoint from `stalledNoPlaceholder`.
   *
   * Split out rather than summed because the two demand different responses:
   * the `limit` scan stays on `insufficient_balance` (the partial index in
   * migration 0028). Name the request ids on the same route instead. Folding
   * both into one number would leave it parked permanently above zero once
   * the scan-backfillable rows are cleared, and a number that never returns
   * to zero is one people stop reading — which is how #1501 stayed invisible.
   *
   * Live rating now writes a placeholder for every unpaid terminal, so a
   * non-zero value here is a historical residue (or a new leak) to name
   * rather than a gauge that should sit above zero.
   *
   * A GAUGE, not a counter — see the note below.
   */
  readonly stalledUnbackfillable?: number;
  /**
   * Request ids for the rows counted in `stalledUnbackfillable`, in page
   * order. One POST, one page. The drain loop unions these across rounds
   * into `cron_run_history` (capped — see
   * {@link STALLED_UNBACKFILLABLE_REQUEST_IDS_CAP}). Optional for
   * rolling-deploy compatibility; absent means an older platform that
   * only reported the count. The authority for a full enumeration is the
   * runbook SQL, not this field.
   */
  readonly stalledUnbackfillableRequestIds?: readonly string[];
  /**
   * Request ids on this page whose usage row resolved to MORE than one
   * revenue_ledger placeholder candidate — a charge and an orphan
   * `unpaid_<request_id>` placeholder for the same request (#2689). The
   * allocator never takes "the last one": it skips a request whose cost is
   * already booked on either, allocates the charged tx otherwise, and withholds
   * (leaves queued) anything else. Every one is logged by request id; the
   * orphan placeholder needs manual cleanup. A GAUGE per page, like the stall
   * counters. Optional for rolling-deploy compatibility (absent = 0).
   */
  readonly placeholderConflicts?: number;
  /**
   * #2695 FL-5: alloc's freeze line as this page applied it. Present only
   * when LEDGER_WRITE_FREEZE_ARMED = "1", alloc's mode is not `off` and the
   * page had pending rows. Observe: `observed` writing plans whose family lay
   * behind the line (committed as before). Enforce: `skipped` such plans, not
   * committed and left queued (`ledger_frozen_writes` `alloc` / `<tx_id>`
   * `open`).
   */
  readonly ledger_write_freeze?: LedgerWriteFreezeSummary;
  /**
   * Usage rows on this page that resolved to a transaction another row of the
   * same page already planned (#2688 same-tx dedupe; batch 2 review A8). They
   * get no plan of their own and are dequeued once the kept row's commit is
   * final — so they left the queue without a ledger write of their own.
   * Production should see 0 (request ids are per-request UUIDs); non-zero
   * means the same request reached the queue twice, and the platform log
   * names the transactions. A per-page count; the drain loop sums it.
   * Optional for rolling-deploy compatibility (absent = 0).
   */
  readonly sameTxDuplicates?: number;
  /**
   * Present (true) on every 200 of {@link BATCH_ALLOCATE_LOCKED_RECARVE_PATH}
   * and only there: the platform that answered carves under the batch locks.
   * The negotiation signal of A10.
   */
  readonly lockedRecarve?: true;
  /**
   * P1a: echo of the request's `shard`, present exactly when it carried one
   * and the page was claimed from that shard (the gateway echoed it too). A
   * platform that predates P1a omits it; control-loops then stops that shard
   * (`stoppedBy: 'shard_not_honored'`) instead of draining whole pages K
   * times over.
   */
  readonly shard?: BatchAllocateShard;
  /** P1a, sharded pages only: wall clock of the page's commit transactions. */
  readonly commitMs?: number;
  /**
   * P1a, sharded pages only: wall clock spent taking the funding-batch row
   * locks of the locked re-carve (`FOR UPDATE`), i.e. waiting behind the other
   * shards on a hot batch. The measure of design §4.2's K_eff.
   */
  readonly lockWaitMs?: number;
  /**
   * P1a, sharded pages only: wall clock spent taking the funding-batch gate
   * SHARED at the start of each commit transaction — waiting behind an
   * overflow catch-up's EXCLUSIVE. Not in `lockWaitMs`.
   */
  readonly gateWaitMs?: number;
  /** P1a, sharded pages only: wall clock of the gateway claim (`/pending`), the shard filter's cost. */
  readonly claimMs?: number;
  /**
   * P1a, sharded pages only: `created_at` of the page's first (oldest) row —
   * the shard's queue head. Comparing shards' heads bounds how far one shard
   * lags the others (the row-level FIFO offset).
   */
  readonly headCreatedAt?: string;
}

/**
 * P1a: a sharded page the platform refused BEFORE committing anything,
 * because the gateway did not echo the shard (it predates P1a and claimed an
 * unsharded page). 200 with `ok: false` so control-loops records it and stops
 * the shard; nothing was written or dequeued.
 */
export interface BatchAllocateShardRefusal {
  readonly ok: false;
  readonly reason: 'shard_not_honored';
  readonly shard: BatchAllocateShard;
  readonly usageRowsProcessed: 0;
  readonly revenueLedgerRowsWritten: 0;
  readonly batchesDepleted: 0;
  readonly overflowCount: 0;
  readonly quarantinedCount: 0;
  readonly markedAllocated: 0;
  readonly lockedRecarve?: true;
}

/**
 * How many stalled-unbackfillable request ids the drain loop persists into
 * `cron_run_history`. The `stalledUnbackfillable` COUNT stays the authority
 * (per-page gauge, max across rounds). The list is a convenience sample.
 *
 * 500 is one allocator page ({@link BATCH_ALLOCATE_MAX_LIMIT}): about 20KB
 * of uuids, the named-backfill default page, and the gauge's own bound. A
 * drain of unique pages could theoretically collect 12 × 500 ids; writing
 * that into `outcomes_json` would bloat a 90-day table. Past the cap the
 * drain loop sets `stalledUnbackfillableRequestIdsTruncated` and the
 * runbook SQL enumerates the rest.
 */
export const STALLED_UNBACKFILLABLE_REQUEST_IDS_CAP = BATCH_ALLOCATE_MAX_LIMIT;

/**
 * ─── Both `stalled*` fields are GAUGES ─────────────────────────────────
 *
 * They report "how many rows are stalled right now", not "how many stall
 * events happened". A caller that drains multiple pages per tick must take the
 * MAXIMUM across its rounds, never the sum.
 *
 * This is not pedantry about metric types. A fully-stalled page marks nothing,
 * so the next round fetches the identical rows and never comes up short — the
 * drain loop runs its full 12 rounds over one unchanging set. Summing turns a
 * real backlog of 521 into a reported 6,000, which is both alarming and wrong,
 * and it moves with the round count rather than with the problem.
 *
 * ─── They are also BOUNDED BY THE PAGE ──────────────────────────────────
 *
 * Both counters only see the rows in the page the allocator fetched, so
 * neither can exceed the request's `limit` (itself capped at
 * {@link BATCH_ALLOCATE_MAX_LIMIT}). A value EQUAL to the page size means
 * "at least this many" — the real backlog may be larger and is not knowable
 * from this response. The incident that motivated these fields had 521 stalled
 * rows and would have reported 500.
 *
 * ─── A ZERO MAY MEAN "NOT MEASURED" ────────────────────────────────────
 *
 * Page truncation is not the only way these numbers understate. Every early
 * return in the handler — a held `ledger_recompute_lock`, an exhausted round
 * budget, a failed page fetch — yields the zero-valued result, so `0` is also
 * what a tick that never inspected a page reports. Read alongside
 * `roundsRun` / `drained`: a zero from a tick with `roundsRun: 0` is the
 * absence of a measurement, not a clean queue. Consumers that alert on these
 * (`upstream_runway_alert`, #1867 P1) must not treat such a zero as a reason
 * to clear an active alert.
 *
 * For the true depth, read the gateway's `allocation_backlog` probe
 * (`internal-health.ts`), which aggregates over the whole queue rather than a
 * page.
 */

// ─── Platform `/internal/ops/capacity-sync` ────────────────────────────

/**
 * Per-upstream capacity transition recorded in the sync response. Only
 * rows where `from !== to` appear; no-op cases are summed in `audited=0`.
 */
export interface CapacityTransition {
  readonly upstream_id: string;
  readonly account_id?: string | null;
  readonly from: 'healthy' | 'depleted';
  readonly to: 'healthy' | 'depleted';
}

export interface CapacitySyncRequest {
  // Reserved for forward compat (e.g. filtering to a specific upstream).
  readonly upstream_id?: string;
}

export interface CapacitySyncResponse {
  readonly ok: true;
  readonly transitions: readonly CapacityTransition[];
  /**
   * Count of `ops_audit` rows this handler enqueued. Equals
   * `transitions.length` on the happy path but surfaced separately so a
   * future audit-disable path doesn't drift the observable contract.
   */
  readonly audited: number;
  /**
   * Postpaid accounts (#2756) whose credit position could not be read: not
   * pushed this tick, so the gateway keeps their current state. Additive —
   * absent when every read succeeded or on an older platform.
   */
  readonly postpaid_errors?: PostpaidReadErrors;
  /**
   * Set when B-16 was armed and the payable fell back to the pre-change
   * statement. Absent when the switch is off, so an off tick matches the
   * body from before this field existed.
   */
  readonly h1_acct_read_fallback?: H1AcctReadFallbackNotice;
}

// ─── Platform `/internal/ops/batch-threshold-check` ────────────────────

/**
 * One alert fired this tick. `percentage` is rounded to 2 decimals for
 * log readability; the authoritative fields on the row are `alerted_80_at`
 * / `alerted_95_at` which the handler stamps before returning.
 */
export interface ThresholdAlert {
  readonly batch_id: string;
  readonly upstream_id: string;
  readonly level: 80 | 95;
  readonly percentage: number;
}

export interface BatchThresholdCheckRequest {
  readonly limit?: number;
}

export interface BatchThresholdCheckResponse {
  readonly ok: true;
  readonly alerts: readonly ThresholdAlert[];
  readonly checked: number;
  /**
   * Postpaid credit-utilisation alerts fired this tick (#2756), same
   * 80% / 95% levels and the same ops_audit channel as the batch alerts.
   * Additive — absent from a platform that predates postpaid terms.
   */
  readonly credit_alerts?: readonly CreditThresholdAlert[];
  /** Postpaid accounts skipped this tick because their position read failed. */
  readonly credit_errors?: PostpaidReadErrors;
  /**
   * Set when B-16 was armed and a credit-position read fell back to the
   * pre-change statement. Absent when the switch is off.
   */
  readonly h1_acct_read_fallback?: H1AcctReadFallbackNotice;
}

// ─── Platform `/internal/ops/overflow-catchup` ─────────────────────────

/**
 * Page size for one catch-up tick, counted in unbatched revenue_ledger
 * rows examined. Deliberately smaller than the allocator's page, and the
 * bound is a concurrency bound rather than a throughput one: the pass
 * holds `FOR UPDATE` on the upstream's live batches for the whole page,
 * so page size is how long a concurrent allocator can be made to wait on
 * that upstream. A catch-up row corrects months-old bookkeeping and has
 * no latency requirement of its own, so draining the production backlog
 * over hours of 5-minute ticks costs nothing.
 */
export const OVERFLOW_CATCHUP_DEFAULT_LIMIT = 200;
export const OVERFLOW_CATCHUP_MAX_LIMIT = 2000;

export interface OverflowCatchupRequest {
  readonly limit?: number;
  /** Restrict the pass to one upstream. Absent → every upstream with debt. */
  readonly upstream_id?: string;
}

/** Per-upstream outcome of one catch-up tick. */
export interface OverflowCatchupUpstreamResult {
  readonly upstream_id: string;
  /** Rows moved from "no batch" onto a funding batch this tick. */
  readonly rows_attributed: number;
  /** Upstream cost carried by those rows, in integer micro-USD. */
  readonly usd_attributed_micros: number;
  /** Batches whose `is_depleted` flipped 0→1 as a result. */
  readonly batches_depleted: number;
  /**
   * Debt still unattributed when the pass stopped, in integer micro-USD.
   * Non-zero means live capacity ran out (or the page did) — the next
   * top-up, or the next tick, continues from the same FIFO position.
   */
  readonly usd_remaining_micros: number;
  /**
   * Rows examined but left as overflow. Two causes, both deliberate:
   * the transaction already holds a row on the target batch (the
   * `(tx_id, source_batch_id)` partial unique index forbids a second one,
   * and merging would rewrite a settled row's cost basis), or the row's
   * `market_rate` cannot support recovering its USD cost. Both stay
   * visible as overflow rather than being silently absorbed.
   */
  readonly rows_skipped: number;
}

export interface OverflowCatchupResponse {
  readonly ok: true;
  readonly results: readonly OverflowCatchupUpstreamResult[];
  /** Rows examined across every upstream — the page-size accounting. */
  readonly rowsExamined: number;
}

// ─── Platform `/internal/ops/unpaid-placeholder-backfill` ──────────────

/**
 * Page size for one backfill run, counted in `usage_rated_events` rows.
 *
 * The default is sized to clear a production-scale backlog in one call (521
 * rows when #1867 was filed) while staying small enough that the run is a
 * bounded write rather than an open-ended table scan. Each inserted row is an
 * independent `ON CONFLICT DO NOTHING` statement, so a page interrupted
 * half-way keeps whatever it committed and the next call resumes from the same
 * FIFO position.
 */
export const UNPAID_PLACEHOLDER_BACKFILL_DEFAULT_LIMIT = 500;
export const UNPAID_PLACEHOLDER_BACKFILL_MAX_LIMIT = 5000;
/** Most `event_days` one named call may name: each is one cold lookup pass over its day. */
export const UNPAID_PLACEHOLDER_BACKFILL_MAX_EVENT_DAYS = 31;
/**
 * Most request-id × cold-day lookups one call may do. The reader counts the
 * lookups it actually opens and refuses a larger count with
 * `cold_lookup_too_large`. The backfill route rejects up front with this same
 * constant, but its count is a conservative estimate: every `event_days`
 * entry is charged for each id that does not name a day, and each UUIDv7 is
 * charged two lookups, even when some of those days are not cold days of the
 * plan. The estimate is therefore ≥ the reader's count. A call the route
 * accepts cannot hit `cold_lookup_too_large` from this product; the route can
 * 400 a call the reader would have answered.
 */
export const COLD_REQUEST_ID_LOOKUP_MAX = 50_000;

export interface UnpaidPlaceholderBackfillRequest {
  readonly limit?: number;
  /**
   * Inclusive lower bound on `usage_rated_events.event_at`. If present
   * and older than the Tokyo usage_logs retention line, the route
   * refuses with `ARCHIVED_WINDOW_FROZEN` — it does not clamp.
   */
  readonly from?: string;
  /**
   * Named request ids to backfill. Mutually exclusive with `limit`: a call
   * that sends both is rejected. Each id is looked up on
   * `usage_rated_events` by primary key; only unpaid terminals
   * (`insufficient_balance` / `unpriced` / `quarantined` / `zero_cost`)
   * that still have no placeholder are written. `charged` is not in that
   * set. Each id is a non-blank string of at most 128 characters.
   */
  readonly request_ids?: readonly string[];
  /**
   * Named mode only, and only while the unified rated reader is on. Off and
   * pair ignore the field (they did before it existed). Shanghai days
   * (`YYYY-MM-DD`, at most `UNPAID_PLACEHOLDER_BACKFILL_MAX_EVENT_DAYS`) of
   * the verdicts' `event_at`, where the reader looks for a named id that is
   * not in the hot table and does not name its own day (a UUIDv4, or an
   * `atk_` / `gvid_` task id). A UUIDv7 names its own day and ignores this
   * list. A UUIDv4 with no list cannot be located and the call is refused
   * with `RATED_READERS_WINDOW_UNAVAILABLE` (`retryable: false`). An `atk_` /
   * `gvid_` id with no list is left out of the locate: the call is not
   * refused, and the id is named in `unresolved_request_ids` (capped). The
   * same id with `event_days` is searched on those days, like a UUIDv4.
   * The lookups — one per listed day for each id that names none, plus at
   * most two per UUIDv7 — must be ≤ `COLD_REQUEST_ID_LOOKUP_MAX`. A larger
   * combination is `INVALID_PAYLOAD`.
   */
  readonly event_days?: readonly string[];
}

/**
 * No `marketRate` field: the rate is resolved PER ROW from the FX sample in
 * force at that row's event instant, so there is no single run-level rate to
 * report. Each written row carries its own in `revenue_ledger.market_rate`.
 */
export interface UnpaidPlaceholderBackfillResponse {
  readonly ok: true;
  /** Rated `insufficient_balance` rows this page found with no placeholder. */
  readonly candidates: number;
  /** Placeholders actually inserted. Re-running a cleared backlog reports 0. */
  readonly inserted: number;
  /**
   * Candidates whose INSERT hit `ON CONFLICT DO NOTHING` — a concurrent rating
   * (or a concurrent run of this route) wrote the same key first. Counted
   * rather than folded into `inserted` so "the backlog was already gone" and
   * "we raced someone" stay distinguishable.
   */
  readonly skippedExisting: number;
  /**
   * Candidates whose INSERT threw. Without this field a run where every row
   * failed returns `candidates: 500, inserted: 0, skippedExisting: 0` — three
   * numbers that do not reconcile and nothing saying why. `candidates` always
   * equals `inserted + skippedExisting + failed`.
   */
  readonly failed: number;
  /**
   * router#2937 follow-up: the rated hot line this run applied. Present only
   * when `RATED_READERS_BOUNDED=on` and the window could reach it.
   */
  readonly rated_hot_line?: string;
  /**
   * `limit` mode only: the scan started at the hot line without an explicit
   * `from`, so older `insufficient_balance` residue may exist but is no longer
   * visible (the verdicts may have been reaped). Absent means the caller
   * named the window.
   */
  readonly possibly_truncated?: boolean;
  /**
   * Requested ids this run did not write, capped at 20. Present when the hot
   * line is on (a named id with no candidate), and also when the unified
   * reader is on and an `atk_` / `gvid_` id had no `event_days` — that id is
   * left out of the cold locate, and the field is the only place the 200
   * names it. The audit row carries the same list.
   */
  readonly unresolved_request_ids?: readonly string[];
  /**
   * #2695 FL-2: the unpaid writer's freeze line as this run applied it.
   * Present only when LEDGER_WRITE_FREEZE_ARMED = "1" and unpaid's mode is
   * not `off`. Under `enforce` the verdicts behind the line (by `rated_at`)
   * are not candidates: `skipped` counts them (the limit scan: at most
   * `limit` of them), and `candidates` stays `inserted + skippedExisting +
   * failed`.
   */
  readonly ledger_write_freeze?: LedgerWriteFreezeSummary;
  /**
   * #2695 FL-2: what the anchor pin did for this call's written rows. Present
   * only when LEDGER_ANCHOR_AT_WRITE = "1". `pinned`: anchors written;
   * `deferred_to_export`: verdicts found but left to their day's export (an
   * old booking day, a day the archive already started, or a verdict too
   * near the rated retention line); `verdict_missing`: the verdict was gone
   * at the write (reaped after the candidate read) — the placeholder is
   * written, its family unanchored.
   */
  readonly ledger_anchor_at_write?: {
    readonly pinned: number;
    readonly deferred_to_export: number;
    readonly verdict_missing: number;
  };
}
